/**
 * KPI detail popover — a focused modal for one metric.
 *
 * Shows the live value + status, a larger sparkline, the description, the full
 * threshold legend (bands consistent with `evaluateStatus`), and pin / Grafana
 * actions. Token-compliant, reduced-motion aware, Esc + backdrop close, focus
 * moves to the dialog on open.
 */
import React from 'react';
import {
  type KpiMeta,
  type KpiPoint,
  STATUS_LABEL,
  evaluateStatus,
  formatKpiValue,
  statusColorVar,
  latestValue,
  seriesValues,
  seriesTrend,
  domainLabel,
  thresholdLegend,
} from './kpiFormat';
import { KpiSparkline } from './KpiSparkline';

export interface KpiDetailPopoverProps {
  meta: KpiMeta;
  points?: KpiPoint[];
  pinned: boolean;
  onClose: () => void;
  onTogglePin?: (id: string) => void;
  reducedMotion?: boolean;
}

export const KpiDetailPopover: React.FC<KpiDetailPopoverProps> = ({
  meta,
  points,
  pinned,
  onClose,
  onTogglePin,
  reducedMotion,
}) => {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [entered, setEntered] = React.useState(!!reducedMotion);

  const value = latestValue(points);
  const status = evaluateStatus(value, meta);
  const color = statusColorVar(status);
  const values = seriesValues(points);
  const trend = seriesTrend(points);
  const display = formatKpiValue(value, meta.format, meta.unit);
  const legend = React.useMemo(() => thresholdLegend(meta), [meta]);
  const trendGlyph = trend === 'up' ? '\u2197' : trend === 'down' ? '\u2198' : '\u2192';

  React.useEffect(() => {
    panelRef.current?.focus();
    if (!reducedMotion) {
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(true);
  }, [reducedMotion]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div
      data-testid="kpi-detail-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'color-mix(in srgb, var(--main-bg, #000) 62%, transparent)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        opacity: entered ? 1 : 0,
        transition: reducedMotion ? 'none' : 'opacity 180ms ease',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${meta.title} details`}
        data-testid={`kpi-detail-${meta.id}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(440px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'var(--card-bg)',
          border: '1px solid var(--card-border, var(--input-border))',
          borderRadius: 16,
          boxShadow: '0 24px 64px color-mix(in srgb, var(--main-bg, #000) 45%, transparent)',
          padding: '20px 22px',
          outline: 'none',
          transform: entered || reducedMotion ? 'none' : 'translateY(10px) scale(0.98)',
          transition: reducedMotion ? 'none' : 'transform 220ms cubic-bezier(0.16,1,0.3,1), opacity 180ms',
        }}
      >
        {/* Status rail */}
        <span
          aria-hidden="true"
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, borderTopLeftRadius: 16, borderBottomLeftRadius: 16, background: color, opacity: status === 'none' ? 0.4 : 0.95 }}
        />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--main-text-secondary)' }}>
              {domainLabel(meta.domain)}
            </div>
            <h3 style={{ margin: '3px 0 0', fontSize: 18, fontWeight: 700, color: 'var(--main-text)', letterSpacing: '-0.01em' }}>
              {meta.title}
            </h3>
          </div>
          <button
            type="button"
            data-testid="kpi-detail-close"
            aria-label="Close details"
            onClick={onClose}
            style={{
              flexShrink: 0, width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, lineHeight: 1, color: 'var(--main-text-secondary)', background: 'transparent',
              border: '1px solid var(--card-border, var(--input-border))', borderRadius: 8, cursor: 'pointer',
            }}
          >
            {'\u00d7'}
          </button>
        </div>

        {/* Value + status pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
          <span
            data-testid="kpi-detail-value"
            style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, color: 'var(--main-text)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}
          >
            {display}
          </span>
          {values.length >= 2 && (
            <span aria-hidden="true" title={`Trend: ${trend}`} style={{ fontSize: 16, fontWeight: 700, color }}>
              {trendGlyph}
            </span>
          )}
          {status !== 'none' && (
            <span
              style={{
                marginLeft: 'auto', padding: '4px 10px', fontSize: 12, fontWeight: 700, borderRadius: 999,
                color, background: `color-mix(in srgb, ${color} 14%, transparent)`,
                border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`,
              }}
            >
              {STATUS_LABEL[status]}
            </span>
          )}
        </div>

        {/* Sparkline */}
        <div style={{ marginTop: 14, color }}>
          <KpiSparkline values={values} color={color} height={64} ariaLabel={`${meta.title} recent trend`} />
        </div>

        {/* Description */}
        {meta.description && (
          <p style={{ margin: '14px 0 0', fontSize: 13, lineHeight: 1.55, color: 'var(--main-text-secondary)' }}>
            {meta.description}
          </p>
        )}

        {/* Threshold legend */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--main-text-secondary)', marginBottom: 8 }}>
            Thresholds
          </div>
          {legend.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--main-text-secondary)', fontStyle: 'italic' }}>
              No thresholds configured — informational metric.
            </div>
          ) : (
            <div data-testid="kpi-detail-legend" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {legend.map((band) => {
                const bandColor = statusColorVar(band.status);
                return (
                  <div key={band.status} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
                    <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: bandColor, flexShrink: 0 }} />
                    <span style={{ fontWeight: 600, color: 'var(--main-text)', minWidth: 64 }}>{band.label}</span>
                    <span style={{ color: 'var(--main-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{band.range}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20 }}>
          {onTogglePin && (
            <button
              type="button"
              data-testid="kpi-detail-toggle-pin"
              onClick={() => onTogglePin(meta.id)}
              aria-pressed={pinned}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                borderRadius: 9, cursor: 'pointer',
                color: pinned ? 'var(--main-text-secondary)' : 'var(--button-primary-text, #fff)',
                background: pinned ? 'transparent' : 'var(--button-primary-bg, var(--quantum-violet, var(--main-accent)))',
                border: pinned ? '1px solid var(--card-border, var(--input-border))' : 'none',
              }}
            >
              <StarGlyph filled={pinned} />
              {pinned ? 'Unpin from Home' : 'Pin to Home'}
            </button>
          )}
          {meta.grafanaUrl && (
            <a
              href={meta.grafanaUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="kpi-detail-grafana"
              style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--main-accent)', textDecoration: 'none' }}
            >
              Open in Grafana {'\u2197'}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

function StarGlyph({ filled }: { filled: boolean }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

export default KpiDetailPopover;
